import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeftIcon, ChevronUpIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext } from "@radix-ui/react-context";
import { Slot } from "@radix-ui/react-slot";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Circle, cn, Copyright, Divider, Eisox, Sheet } from "..";
import { Popover } from "../Popover";
import * as Tooltip from "../Tooltip";
import christmasHat from "./christmas_hat.png";

/* -------------------------------------------------------------------------------------------------
 * SidebarProvider
 * -----------------------------------------------------------------------------------------------*/

const SIDEBAR_NAME = "Sidebar";
interface SidebarContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenToggle: () => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onCollapseToggle: () => void;
  isMobile: boolean;
}

const [SidebarProvider, useSidebarContext] = createContext<SidebarContextValue>(SIDEBAR_NAME);

interface ProviderProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  defaultCollapsed?: boolean;
  children?: React.ReactNode;
}

const Provider: React.FC<ProviderProps> = props => {
  const {
    open: openProp,
    onOpenChange,
    defaultOpen = false,
    collapsed: collapsedProp,
    onCollapsedChange,
    defaultCollapsed = false,
    children,
  } = props;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });
  const [collapsed, setCollapsed] = useControllableState({
    prop: collapsedProp,
    onChange: (collapsed: boolean) => {
      onCollapsedChange?.(collapsed);
    },
    defaultProp: defaultCollapsed,
  });

  return (
    <Tooltip.Provider>
      <SidebarProvider
        open={open}
        onOpenChange={setOpen}
        onOpenToggle={useCallback(() => setOpen(prev => !prev), [setOpen])}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        onCollapseToggle={useCallback(() => setCollapsed(prev => !prev), [setCollapsed])}
        isMobile={isMobile}
      >
        <div className="flex h-full w-full overflow-hidden">{children}</div>
      </SidebarProvider>
    </Tooltip.Provider>
  );
};

Provider.displayName = "SidebarProvider";

/* -------------------------------------------------------------------------------------------------
 * Sidebar
 * -----------------------------------------------------------------------------------------------*/

interface SidebarProps extends React.ComponentPropsWithRef<"div"> {
  displayChristmasHat?: boolean;
  overlay?: {
    className?: string;
    style?: React.CSSProperties;
  };
}

const Root = forwardRef<HTMLDivElement, SidebarProps>((props, forwardedRef) => {
  const { displayChristmasHat = false, className, children, overlay, ...sidebarProps } = props;
  const { open, onOpenChange, isMobile, collapsed, onCollapseToggle } = useSidebarContext(SIDEBAR_NAME);

  const handleFullScreen = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    const doc = window.document;
    const docEl = doc.documentElement;
    if (!doc.fullscreenElement) {
      void docEl.requestFullscreen.call(docEl);
    } else {
      void doc.exitFullscreen.call(doc);
    }
  };

  if (isMobile) {
    return (
      <Sheet.Root open={open} onOpenChange={onOpenChange}>
        <Sheet.Portal>
          <Sheet.Overlay {...overlay} className={cn("z-1300", overlay?.className)} />
          <Sheet.Content
            {...sidebarProps}
            side="left"
            className={cn(
              `md:relative md:h-dvh md:shadow-none md:transition-[width] md:duration-300 md:ease-in-out z-1300 flex h-[calc(100dvh-20px)] w-[250px] flex-none flex-col bg-primary-white`,
              collapsed && "md:w-[calc(2*40px+21px)]",
              className,
            )}
          >
            <Eisox
              className={cn("mx-auto my-[25px] w-full cursor-pointer", collapsed && "[&>*:not(:first-child)]:hidden")}
            />
            {children}
          </Sheet.Content>
        </Sheet.Portal>
      </Sheet.Root>
    );
  }

  return (
    <div
      {...sidebarProps}
      ref={forwardedRef}
      className={cn(
        `md:relative md:h-dvh md:shadow-none md:transition-[width] md:duration-300 md:ease-in-out flex h-[calc(100dvh-20px)] w-[250px] flex-none flex-col bg-primary-white`,
        collapsed && "md:w-[calc(2*40px+21px)]",
        className,
      )}
    >
      <Eisox
        width={collapsed ? "31" : "133"}
        viewBox={collapsed ? "0 0 31 30" : "0 0 133 30"}
        className={cn("mx-auto my-[25px] w-full cursor-pointer", collapsed && "[&>*:not(:first-child)]:hidden")}
        onClick={handleFullScreen}
      />
      {displayChristmasHat && (
        <img
          src={christmasHat}
          className={cn("absolute top-[9px] left-[35px] w-[60px] object-cover", collapsed && "sm:left-[11px]")}
        />
      )}
      <Circle.Root
        size={30}
        backgroundColor="white"
        color="darkBlue"
        className={cn(
          `sm:absolute sm:top-[25px] sm:left-full sm:-translate-x-1/2 sm:cursor-pointer sm:shadow-[var(--shadow-[var(--shadow-popover)])] sm:[&>svg]:transition-transform sm:[&>svg]:duration-300 sm:[&>svg]:ease-in-out`,
          collapsed && "sm:[&>svg]:rotate-180",
        )}
        onClick={onCollapseToggle}
      >
        <ChevronLeftIcon />
      </Circle.Root>
      {children}
    </div>
  );
});

Root.displayName = "Sidebar";

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

interface TriggerProps extends React.ComponentPropsWithRef<"button"> {
  asChild?: boolean;
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>((props, forwardedRef) => {
  const { asChild, onClick, ...triggerProps } = props;
  const { onOpenToggle } = useSidebarContext(SIDEBAR_NAME);
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      {...triggerProps}
      ref={forwardedRef}
      onClick={e => {
        onClick?.(e);
        onOpenToggle();
      }}
    />
  );
});

Trigger.displayName = "SidebarTrigger";

/* -------------------------------------------------------------------------------------------------
 * Header
 * -----------------------------------------------------------------------------------------------*/

interface ChildrenProps {
  collapsed: boolean;
  isMobile: boolean;
}

interface HeaderProps extends Omit<React.ComponentPropsWithRef<"div">, "children"> {
  asChild?: boolean;
  children?: React.ReactNode | ((state: ChildrenProps) => React.ReactNode);
}

const Header = forwardRef<HTMLDivElement, HeaderProps>((props, forwardedRef) => {
  const { asChild, className, children, ...headerProps } = props;
  const { collapsed, isMobile } = useSidebarContext(SIDEBAR_NAME);
  const Comp = asChild ? Slot : "div";

  return (
    <Comp {...headerProps} ref={forwardedRef} className={cn("w-full", className)} data-collapsed={collapsed}>
      {typeof children === "function" ? children({ collapsed, isMobile }) : children}
    </Comp>
  );
});

Header.displayName = "SidebarHeader";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "SidebarContent";

interface ContentContextValue {
  selectedItem: ItemElement | null;
  onSelectedItemChange: (selectedItem: ItemElement) => void;
  itemRefCallback?: (node: HTMLButtonElement | null, active: boolean, disabled: boolean) => void;
}

const [ContentProvider, useContentContext] = createContext<ContentContextValue>(CONTENT_NAME);

type ContentProps = React.ComponentPropsWithRef<"div">;

const Content = forwardRef<HTMLDivElement, ContentProps>((props, forwardedRef) => {
  const { className, children, ...contentProps } = props;
  const firstValidItemFoundRef = useRef(false);
  const markerRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<ItemElement | null>(null);

  const itemRefCallback = useCallback((node: HTMLButtonElement | null, active: boolean, disabled: boolean) => {
    const isFirstValidItem = !firstValidItemFoundRef.current && !disabled;
    const isSelectedItem = active;
    if (isFirstValidItem || isSelectedItem) {
      firstValidItemFoundRef.current = true;
      setSelectedItem(node);
    }
  }, []);

  useEffect(() => {
    if (selectedItem && markerRef.current) {
      const marker = markerRef.current;
      marker.style.top = `${selectedItem.offsetTop}px`;
      marker.style.height = `${selectedItem.offsetHeight}px`;
    }
  }, [selectedItem, markerRef]);

  return (
    <ContentProvider
      selectedItem={selectedItem}
      onSelectedItemChange={setSelectedItem}
      itemRefCallback={itemRefCallback}
    >
      <div
        {...contentProps}
        ref={forwardedRef}
        className={cn("gap-3.75 px-10 py-2.5 relative flex flex-1 flex-col overflow-y-auto", className)}
      >
        {children}
        <div ref={markerRef} className={"right-0 w-0.75 absolute bg-primary-blue transition-all duration-300"} />
      </div>
    </ContentProvider>
  );
});

Content.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Group
 * -----------------------------------------------------------------------------------------------*/

const GROUP_NAME = "SidebarGroup";

interface GroupProps extends React.ComponentPropsWithRef<"div"> {
  label: string;
}

const Group = forwardRef<HTMLDivElement, GroupProps>((props, forwardedRef) => {
  const { label, className, children, ...groupProps } = props;
  const { collapsed } = useSidebarContext(GROUP_NAME);

  return (
    <div
      {...groupProps}
      ref={forwardedRef}
      className={cn("gap-2.5 flex flex-col", className)}
      data-collapsed={collapsed}
    >
      {!collapsed && (
        <p className={"m-0 font-semibold text-sm text-primary-dark-gray uppercase select-none"}>{label}</p>
      )}
      {children}
    </div>
  );
});

Group.displayName = GROUP_NAME;

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

const ITEM_NAME = "SidebarItem";

type ItemElement = React.ElementRef<typeof Item>;

interface ItemProps extends React.ComponentPropsWithRef<"button"> {
  asChild?: boolean;
  isActive?: boolean;
}

const Item = forwardRef<HTMLButtonElement, ItemProps>((props, forwardedRef) => {
  const { asChild, isActive, disabled, onClick, className, ...itemProps } = props;
  const { collapsed, onOpenChange, isMobile } = useSidebarContext(ITEM_NAME);
  const { selectedItem, onSelectedItemChange, itemRefCallback } = useContentContext(ITEM_NAME);

  const textContent = useRef<string>("");
  const itemRef = useRef<HTMLButtonElement>(null);
  // When `isActive` is passed (router), it wins over the last click — in-page links never hit Item.onClick.
  const selected = isActive ?? (selectedItem !== null && itemRef.current !== null && selectedItem === itemRef.current);
  const composedRefs = useComposedRefs(
    forwardedRef,
    itemRef,
    node => itemRefCallback?.(node, selected, !!disabled),
    node => {
      textContent.current = node?.textContent ?? "";
    },
  );

  const Comp = asChild ? Slot : "button";

  const button = (
    <Comp
      {...itemProps}
      ref={composedRefs}
      className={cn(
        `gap-2.5 font-semibold flex shrink-0 cursor-pointer flex-nowrap items-center overflow-hidden text-base whitespace-nowrap text-primary-dark-gray disabled:cursor-default data-[active=true]:text-primary-dark-blue [&>svg]:h-[21px] [&>svg]:w-[21px] [&>svg]:shrink-0 data-[active=true]:[&>svg]:text-primary-blue`,
        className,
      )}
      onClick={e => {
        onClick?.(e);
        if (isMobile) {
          onOpenChange(false);
        }
        if (itemRef.current) {
          onSelectedItemChange(itemRef.current);
        }
      }}
      disabled={disabled}
      data-active={selected}
      data-collapsed={collapsed}
    />
  );

  useEffect(() => {
    if (itemRef.current && isActive) {
      onSelectedItemChange(itemRef.current);
    }
  }, [isActive, onSelectedItemChange]);

  if (collapsed && !isMobile) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className={"px-2.5 py-1.25 font-medium z-1300 rounded-[5px]"} side="right" sideOffset={20}>
            {textContent.current}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  return button;
});

Item.displayName = "SidebarItem";

/* -------------------------------------------------------------------------------------------------
 * Footer
 * -----------------------------------------------------------------------------------------------*/

interface FooterProps {
  email: string;
  role?: string;
  version: string;
  firstName?: string;
  lastName?: string;
  chip?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  defaultExpanded?: boolean;
}

const Footer: React.FC<FooterProps> = props => {
  const {
    email,
    role,
    version,
    firstName,
    lastName,
    chip,
    className,
    style,
    children,
    expanded: expandedProp,
    onExpandedChange,
    defaultExpanded = false,
  } = props;
  const { collapsed, isMobile } = useSidebarContext(SIDEBAR_NAME);

  const [expanded, setExpanded] = useControllableState({
    prop: expandedProp,
    onChange: onExpandedChange,
    defaultProp: defaultExpanded,
  });

  if (collapsed && !isMobile) {
    const fallback = (firstName && lastName ? `${firstName[0]}${lastName[0]}` : email[0])?.toUpperCase();

    return (
      <div className={"p-5 box-border flex w-full items-center justify-center bg-primary-white"}>
        <Popover.Root>
          <Popover.Trigger
            className={`h-12.5 w-12.5 flex items-center justify-center rounded-[8px] bg-primary-darker-gray text-primary-dark-gray`}
          >
            {fallback}
          </Popover.Trigger>
          <Popover.Content className={"z-1300 w-[250px]"} align="end" side="right" sideOffset={30}>
            <div className={"overflow-hidden"}>
              <p className={"font-medium overflow-hidden text-ellipsis whitespace-nowrap select-none"}>
                {firstName && lastName ? `${firstName} ${lastName}` : email}
              </p>
              {role && (
                <p
                  className={
                    "overflow-hidden text-base text-ellipsis whitespace-nowrap text-primary-dark-gray select-none"
                  }
                >
                  {role}
                </p>
              )}
            </div>
            <Divider className={"ml-[-20px] w-[calc(100%+40px)]"} />
            {children}
            <Copyright className={"mt-2.5 text-sm"} version={version} />
          </Popover.Content>
        </Popover.Root>
      </div>
    );
  }

  return (
    <Collapsible.Root
      open={expanded}
      onOpenChange={setExpanded}
      className={
        "p-5 box-border w-full rounded-b-[10px] border-t-2 border-solid border-primary-darker-gray bg-primary-white"
      }
    >
      <Collapsible.Trigger
        className={`[&>svg]:ease-in-out flex w-full cursor-pointer items-center justify-between [&>svg]:transition-transform [&>svg]:duration-300 data-[state=closed]:[&>svg]:rotate-0 data-[state=open]:[&>svg]:-rotate-180`}
      >
        <div className={"flex flex-col items-start overflow-hidden text-md"}>
          <p className={"font-medium overflow-hidden text-ellipsis whitespace-nowrap select-none"}>
            {firstName && lastName ? `${firstName} ${lastName}` : email}
          </p>
          {role && (
            <p
              className={"overflow-hidden text-base text-ellipsis whitespace-nowrap text-primary-dark-gray select-none"}
            >
              {role}
            </p>
          )}
        </div>
        {!expanded && chip}
        <ChevronUpIcon />
      </Collapsible.Trigger>
      <Collapsible.Content
        className={cn(
          "mt-2.5 overflow-hidden",
          expanded ? "animate-sidebar-slide-up" : "animate-sidebar-slide-down",
          className,
        )}
        style={style}
      >
        {children}
        <Copyright className={"mt-2.5 text-sm"} version={version} />
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

Footer.displayName = "SidebarFooter";

export { Content, Footer, Group, Header, Item, Provider, Root, Trigger };
export type {
  ContentProps,
  FooterProps,
  GroupProps,
  HeaderProps,
  ItemProps,
  ProviderProps,
  SidebarProps,
  TriggerProps,
};
